import { XMarkIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import { useState } from "react";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";
import { typographyVariants } from "@/core/components/ui/typography/typography";
import { extractErrorMessage } from "@/core/api/errors";
import { cn } from "@/core/utils/utils";
import { DeleteDocumentModal } from "./delete-document-modal";
import { DocumentIconFor } from "./document-icon";
import { formatFileSize } from "./document-utils";
import { useDeleteOnboardingDocument } from "./query";
import type { OnboardingDocument } from "./types";

interface DocumentListProps {
  businessId: string;
  documents: OnboardingDocument[];
}

export const DocumentList = ({ businessId, documents }: DocumentListProps) => {
  const [pendingDelete, setPendingDelete] = useState<OnboardingDocument | null>(
    null,
  );
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteMutation = useDeleteOnboardingDocument(businessId);

  const closeModal = () => {
    setPendingDelete(null);
    setDeleteError(null);
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;

    setDeleteError(null);
    deleteMutation.mutate(pendingDelete.id, {
      onSuccess: closeModal,
      onError: (error) => {
        setDeleteError(extractErrorMessage(error).message);
      },
    });
  };

  return (
    <>
      {/* Its own provider, so the list works wherever it is rendered rather than only
          under the app shell's. Nested providers are fine; this one just sets a shorter
          delay than a page-level tooltip wants. */}
      <TooltipProvider delayDuration={200}>
        <Stack gap="none" align="stretch" fullWidth>
          {documents.map((document) => {
            const details = [
              document.content_type,
              formatFileSize(document.file_size),
            ].filter(Boolean);
            const name = document.file_name || document.title;

            return (
              <Stack
                key={document.id}
                direction="row"
                align="center"
                gap="md"
                fullWidth
                className="border-b border-stroke-tertiary py-3 last:border-b-0"
              >
                <DocumentIconFor
                  contentType={document.content_type}
                  fileName={name}
                  className="h-5 w-5 shrink-0 text-tertiary-text"
                />
                {/* align stretch, or the flex column sizes to its longest child and a
                  generated filename runs straight through the delete button. */}
                <Stack gap="none" align="stretch" className="min-w-0 flex-1">
                  {/* A generated filename is wider than the row, so it truncates. The full
                    name stays readable on hover: a native title tooltip is slow and easy
                    to miss, and the truncation is visual only, so assistive tech still
                    gets the whole string from the element itself. */}
                  <Tooltip>
                    {/* A span rather than Typography: Radix anchors the tooltip to the
                        trigger's ref, and Typography does not forward one, which left the
                        tooltip positioned off the top of the page. */}
                    <TooltipTrigger asChild>
                      <span
                        className={cn(
                          typographyVariants({ variant: "bodyW1" }),
                          "block cursor-default truncate text-left",
                        )}
                      >
                        {name}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent maxWidth="xl" className="break-all">
                      {name}
                    </TooltipContent>
                  </Tooltip>
                  <Typography variant="captionW1" textColor="tertiary">
                    {[
                      dayjs(document.uploaded_at).format("MMM D [at] h:mm A"),
                      ...details,
                    ].join(" · ")}
                  </Typography>
                </Stack>
                {document.deletable && (
                  <Button
                    variant="unstyled"
                    aria-label={`Delete ${name}`}
                    className="shrink-0 rounded p-1 text-tertiary-text hover:text-primary-text"
                    onClick={() => {
                      setDeleteError(null);
                      setPendingDelete(document);
                    }}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </Button>
                )}
              </Stack>
            );
          })}
        </Stack>
      </TooltipProvider>

      <DeleteDocumentModal
        document={pendingDelete}
        isDeleting={deleteMutation.isPending}
        error={deleteError}
        onCancel={closeModal}
        onConfirm={confirmDelete}
      />
    </>
  );
};
